import { TrendingUp, TrendingDown, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

// ─── Types ──────────────────────────────────────────────────────────────────

type CardVariant = 'default' | 'success' | 'warning' | 'danger';

interface KPIStatCardProps {
  title: string;
  value: number | string;
  icon: LucideIcon;
  variant?: CardVariant;
  trend?: number;
  onClick?: () => void;
  loading?: boolean;
}

// ─── Variant styles ──────────────────────────────────────────────────────────

const variantStyles: Record<CardVariant, string> = {
  default: 'bg-card text-card-foreground border border-border',
  success: 'bg-emerald-50 text-emerald-900 border border-emerald-200 dark:bg-emerald-950 dark:text-emerald-100 dark:border-emerald-800',
  warning: 'bg-amber-50 text-amber-900 border border-amber-200 dark:bg-amber-950 dark:text-amber-100 dark:border-amber-800',
  danger: 'bg-red-50 text-red-900 border border-red-200 dark:bg-red-950 dark:text-red-100 dark:border-red-800',
};

const iconStyles: Record<CardVariant, string> = {
  default: 'text-muted-foreground',
  success: 'text-emerald-600 dark:text-emerald-400',
  warning: 'text-amber-600 dark:text-amber-400',
  danger: 'text-red-600 dark:text-red-400',
};

// ─── Skeleton ───────────────────────────────────────────────────────────────

function KPIStatCardSkeleton() {
  return (
    <div
      className="rounded-lg border border-border bg-card p-4 space-y-3"
      role="status"
      aria-label="Loading stat"
    >
      <div className="flex justify-between items-center">
        <div className="h-4 w-24 animate-pulse rounded bg-muted" aria-hidden="true" />
        <div className="h-5 w-5 animate-pulse rounded bg-muted" aria-hidden="true" />
      </div>
      <div className="h-8 w-16 animate-pulse rounded bg-muted" aria-hidden="true" />
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Reusable KPI stat card for dashboard header row.
 * Pass onClick to make the card interactive (e.g., Awaiting Review → Needs Review tab).
 */
export function KPIStatCard({
  title,
  value,
  icon: Icon,
  variant = 'default',
  trend,
  onClick,
  loading = false,
}: KPIStatCardProps) {
  if (loading) return <KPIStatCardSkeleton />;

  const isClickable = typeof onClick === 'function';

  return (
    <div
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
      className={cn(
        'rounded-lg p-4 space-y-2 transition-shadow',
        variantStyles[variant],
        isClickable && 'cursor-pointer hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
      aria-label={isClickable ? `${title}: ${value}. Click to view` : undefined}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium opacity-80">{title}</span>
        <Icon className={cn('h-4 w-4', iconStyles[variant])} aria-hidden="true" />
      </div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
      {trend !== undefined && (
        <div className="flex items-center gap-1 text-xs opacity-70">
          {trend >= 0
            ? <TrendingUp className="h-3 w-3" aria-hidden="true" />
            : <TrendingDown className="h-3 w-3" aria-hidden="true" />}
          <span>{Math.abs(trend)}% vs last month</span>
        </div>
      )}
    </div>
  );
}
